import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Mail,
  Phone,
  Calendar,
  Briefcase,
  Star,
  Award,
  User,
  Pencil,
  IdCard,
  ShieldCheck,
  Trophy,
  Coins,
  Settings,
  Trash2,
  Ban,
  BadgeCheck ,
} from "lucide-react";

import MemberCard from "../components/MemberCard";

export default function MemberDetails() {
  const [showCard, setShowCard] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);

  const [actionType, setActionType] = useState(null);
  const [reason, setReason] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();

  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  const [presences, setPresences] = useState([]);
  const [stats, setStats] = useState({
    presents: 0,
    absents: 0,
    rate: 0,
  });

  useEffect(() => {
    loadMember();
  }, []);
  async function loadMember() {
    try {
      setLoading(true);

      const token = localStorage.getItem("membreToken");

      const res = await fetch(`/api/admin/member?action=details&id=${id}`, {
        headers: {
          Authorization: "Bearer " + token,
        },
      });

      const json = await res.json();

      if (!json.success) {
        throw new Error(json.message);
      }

      setMember(json.member);

      await loadHistory(json.member.member_id);
    } catch (err) {
      console.error(err);

      alert(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadHistory(memberId) {
    const token = localStorage.getItem("membreToken");

    const res = await fetch(
      `/api/admin/member?action=history&member_id=${memberId}`,
      {
        headers: {
          Authorization: "Bearer " + token,
        },
      },
    );

    const json = await res.json();

    if (!json.success) return;

    setPresences(json.history);

    setStats(json.stats);
  }
  async function handleMemberAction() {
    try {
      if (
        (actionType === "suspend" || actionType === "delete") &&
        !reason.trim()
      ) {
        alert("Veuillez saisir une raison.");
        return;
      }
      setLoadingAction(true);

      const token = localStorage.getItem("membreToken");

      let action = "";

      if (actionType === "suspend") action = "suspend";

      if (actionType === "reactivate") action = "reactivate";

      if (actionType === "delete") action = "delete";

      const res = await fetch(`/api/admin/member?action=${action}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id: member.id,
          reason,
          sendEmail,
        }),
      });

      const json = await res.json();

      if (!json.success) {
        alert(json.message);
        return;
      }

      alert(json.message);

      navigate("/admin/members");
    } catch (err) {
      console.error(err);
      alert("Erreur serveur");
    } finally {
      setLoadingAction(false);
    }
  }

  function level(points) {
    if (points >= 600) return "Elite";
    if (points >= 300) return "Gold";
    if (points >= 100) return "Silver";
    return "Bronze";
  }

  if (loading) {
    return <div className="p-10 text-center">Chargement...</div>;
  }

  if (!member) {
    return <div className="p-10 text-center">Membre introuvable</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 mb-6 bg-white px-4 py-2 rounded-xl shadow"
        >
          <ArrowLeft size={18} />
          Retour
        </button>

        <div className="bg-white rounded-3xl shadow-xl p-8">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
            <img
              src={member.avatar || "/images/default-avatar.png"}
              alt=""
              className="w-40 h-40 rounded-full object-cover border-4 border-blue-600 shadow-lg"
            />

            <div className="flex-1">
              <h1 className="text-3xl font-extrabold">
                {member.prenom} {member.nom}
              </h1>

              <p className="text-gray-500 mt-1">{member.member_id}</p>

              <div className="mt-4 flex flex-wrap gap-3">
                <span className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm">
                  {level(member.points || 0)}
                </span>

                <span className="bg-yellow-500 text-white px-4 py-2 rounded-full text-sm">
                  ⭐ {member.points || 0} Points
                </span>
              </div>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 rounded-2xl p-5 shadow">
              <h2 className="text-xl font-bold mb-5">
                Informations personnelles
              </h2>

              <div className="space-y-5">
                <Info
                  icon={<Mail size={20} />}
                  label="Email"
                  value={member.email}
                />

                <Info
                  icon={<Phone size={20} />}
                  label="Téléphone"
                  value={member.telephone || "—"}
                />

                <Info
                  icon={<Calendar size={20} />}
                  label="Date de naissance"
                  value={
                    member.birthday
                      ? new Date(member.birthday).toLocaleDateString("fr-FR")
                      : "—"
                  }
                />

                <Info
                  icon={<Briefcase size={20} />}
                  label="Occupation"
                  value={member.occupation || "—"}
                />

                <Info
                  icon={<Award size={20} />}
                  label="Formule"
                  value={member.formule || "—"}
                />
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-5 shadow">
              <h2 className="text-xl font-bold mb-5">Profil du membre</h2>

              <div className="space-y-5">
                <Info
                  icon={<User size={20} />}
                  label="Motivation"
                  value={member.motivation || "Aucune"}
                />

                <Info
                  icon={<Star size={20} />}
                  label="Centres d'intérêt"
                  value={member.interets || "Aucun"}
                />

                <Info
                  icon={<BadgeCheck  size={20} />}
                  label="Fonction"
                  value={member.fonction || "Membre Actif"}
                />

                <Info
                  icon={<Trophy size={20} />}
                  label="Niveau"
                  value={level(member.points || 0)}
                />

                <Info
                  icon={<Coins size={20} />}
                  label="Points"
                  value={`${member.points || 0} pts`}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-8">
            <StatCard
              title="Présences"
              value={stats.presents}
              color="bg-green-500"
            />

            <StatCard
              title="Absences"
              value={stats.absents}
              color="bg-red-500"
            />

            <StatCard
              title="Présence"
              value={`${stats.rate}%`}
              color="bg-blue-600"
            />

            <StatCard
              title="Points"
              value={member.points || 0}
              color="bg-yellow-500"
            />
          </div>

          <div className="mt-10 bg-white rounded-2xl shadow overflow-hidden">
            <div className="px-6 py-5 border-b">
              <h2 className="text-xl font-bold">Historique des présences</h2>
            </div>

            {presences.length === 0 ? (
              <div className="p-10 text-center text-gray-500">
                Aucun historique disponible.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left p-4">Date</th>

                      <th className="text-left p-4">Heure</th>

                      <th className="text-left p-4">Statut</th>
                    </tr>
                  </thead>

                  <tbody>
                    {presences.map((item) => (
                      <tr key={item.id} className="border-t hover:bg-gray-50">
                        <td className="p-4">
                          {new Date(item.date).toLocaleDateString("fr-FR")}
                        </td>

                        <td className="p-4">
                          {item.scanned_at
                            ? new Date(item.scanned_at).toLocaleTimeString(
                                "fr-FR",
                              )
                            : "--:--"}
                        </td>

                        <td className="p-4">
                          {item.status === "present" ? (
                            <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                              Présent
                            </span>
                          ) : (
                            <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-semibold">
                              Absent
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-4">
            <button
              onClick={() =>
                navigate(`/admin/edit-member/${member.id}`, {
                  state: {
                    member,
                  },
                })
              }
              className="bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold transition flex items-center justify-center gap-2"
            >
              <Pencil size={18} />
              Modifier
            </button>

            <button
              onClick={() => setShowCard(true)}
              className="bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-semibold transition flex items-center justify-center gap-2"
            >
              <IdCard size={18} />
              Voir Carte
            </button>
            <button
              onClick={() => setShowManageModal(true)}
              className="bg-red-600 hover:bg-red-700 text-white py-3 rounded-xl font-semibold transition flex items-center justify-center gap-2"
            >
              <Settings size={18} />
              Gestion du compte
            </button>

            <button
              onClick={() => navigate(-1)}
              className="bg-gray-700 hover:bg-black text-white py-3 rounded-xl font-semibold transition flex items-center justify-center gap-2"
            >
              <ArrowLeft size={18} />
              Retour
            </button>
          </div>
        </div>
      </div>
      {showManageModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b">
              <h2 className="text-2xl font-bold">Gestion du membre</h2>

              <p className="text-gray-500 mt-2">
                Choisissez une action pour ce membre.
              </p>
            </div>

            <div className="p-6 space-y-4">
              <button
                onClick={() => {
                  if (member.status === "suspended") {
                    setActionType("reactivate");
                  } else {
                    setActionType("suspend");
                  }

                  setReason("");
                  setSendEmail(true);
                  setShowManageModal(false);
                }}
                className="w-full border rounded-2xl p-5 hover:bg-yellow-50 transition text-left"
              >
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                    {member.status === "suspended" ? (
                      <ShieldCheck className="text-green-600" />
                    ) : (
                      <Ban className="text-yellow-600" />
                    )}
                  </div>

                  <div>
                    <h3 className="font-bold text-lg">
                      {member.status === "suspended"
                        ? "Réactiver le membre"
                        : "Suspendre le membre"}
                    </h3>

                    <p className="text-gray-500 text-sm mt-1">
                      {member.status === "suspended"
                        ? "Le membre pourra de nouveau accéder à son compte."
                        : "Le membre ne pourra plus accéder à son compte."}
                    </p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => {
                  setActionType("delete");
                  setShowManageModal(false);
                }}
                className="w-full border rounded-2xl p-5 hover:bg-red-50 transition text-left"
              >
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <Trash2 className="text-red-600" />
                  </div>

                  <div>
                    <h3 className="font-bold text-lg text-red-600">
                      Supprimer définitivement
                    </h3>

                    <p className="text-gray-500 text-sm mt-1">
                      Cette action est irréversible.
                    </p>
                  </div>
                </div>
              </button>
            </div>

            <div className="border-t p-5">
              <button
                onClick={() => setShowManageModal(false)}
                className="w-full bg-gray-800 hover:bg-black text-white py-3 rounded-xl font-semibold"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
      {actionType && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg">
            <div className="p-6 border-b">
              <h2 className="text-2xl font-bold">
                {actionType === "suspend"
                  ? "Suspendre le membre"
                  : "Supprimer définitivement"}
              </h2>
            </div>

            <div className="p-6">
              <label className="font-semibold">Raison</label>

              <textarea
                rows={5}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full mt-2 border rounded-xl p-3 resize-none"
                placeholder="Expliquez la raison..."
              />

              <label className="flex items-center gap-3 mt-5">
                <input
                  type="checkbox"
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                />
                Envoyer un email au membre
              </label>
            </div>

            <div className="border-t p-6 flex gap-3">
              <button
                onClick={() => setActionType(null)}
                className="flex-1 bg-gray-700 text-white py-3 rounded-xl"
              >
                Annuler
              </button>

              <button
                onClick={handleMemberAction}
                disabled={loadingAction}
                className={`flex-1 py-3 rounded-xl text-white font-semibold ${
                  actionType === "delete"
                    ? "bg-red-600 hover:bg-red-700"
                    : actionType === "reactivate"
                      ? "bg-green-600 hover:bg-green-700"
                      : "bg-yellow-500 hover:bg-yellow-600"
                }`}
              >
                {loadingAction
                  ? "Traitement..."
                  : actionType === "delete"
                    ? "Supprimer"
                    : actionType === "reactivate"
                      ? "Réactiver"
                      : "Suspendre"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showCard && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-3xl p-6 shadow-2xl max-w-md w-full">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-xl font-bold">Carte du membre</h2>

              <button onClick={() => setShowCard(false)} className="text-2xl">
                ✕
              </button>
            </div>

            <MemberCard user={member} />
          </div>
        </div>
      )}
    </div>
  );
}
function Info({ icon, label, value }) {
  let displayValue = value;

  if (label === "Centres d'intérêt") {
    if (Array.isArray(value)) {
      displayValue = value.join(" • ");
    } else if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);

        if (Array.isArray(parsed)) {
          displayValue = parsed.join(" • ");
        } else {
          displayValue = value;
        }
      } catch {
        displayValue = value.replace(/,/g, " • ").replace(/;/g, " • ");
      }
    }
  }

  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 flex-shrink-0">
        {icon}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500">{label}</p>

        <p
          className="font-semibold whitespace-pre-wrap break-words"
          style={{
            wordBreak: "normal",
            overflowWrap: "anywhere",
          }}
        >
          {displayValue}
        </p>
      </div>
    </div>
  );
}

function StatCard({ title, value, color }) {
  return (
    <div className={`${color} rounded-2xl p-5 text-white shadow`}>
      <p className="text-sm opacity-80">{title}</p>

      <h2 className="text-3xl font-bold mt-2">{value}</h2>
    </div>
  );
}
